import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Download, Settings2 } from "lucide-react";

const inputCls = "bg-[#0b0811] border-white/10 text-white text-sm";
const Label = ({ children }) => <label className="text-[10px] uppercase text-slate-500">{children}</label>;

function Num({ value, onChange, testId, step = "1", className = "" }) {
  return <Input type="number" step={step} className={`${inputCls} ${className}`} value={value ?? ""}
    onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testId} />;
}

export default function HypothesesForm({ api, societeId, initialHyp, initialName, onSave, saving }) {
  const [name, setName] = useState(initialName || "");
  const [hyp, setHyp] = useState(initialHyp);
  const [prefilling, setPrefilling] = useState(false);
  const H = (key, val) => setHyp(h => ({ ...h, [key]: val }));
  const HE = (key, val) => setHyp(h => ({ ...h, emprunt: { ...h.emprunt, [key]: val } }));
  const G = (i, val) => setHyp(h => {
    const c = [...(h.croissance_pct || [0, 0, 0])];
    c[i] = val;
    return { ...h, croissance_pct: c };
  });

  const prefill = async () => {
    setPrefilling(true);
    try {
      const { data } = await api.get(`/previsionnel/base-data?societe_id=${societeId}`);
      setHyp(h => ({
        ...h,
        ca_base_mensuel: data.ca_mensuel_moyen,
        charges_fixes_mensuelles: data.depenses_mensuelles_moyennes,
        masse_salariale_mensuelle: data.masse_salariale_mensuelle,
        tresorerie_initiale: Math.max(0, data.tresorerie_initiale),
      }));
      if (!data.ca_mensuel_moyen && !data.depenses_mensuelles_moyennes) {
        toast.warning(`Peu de données réelles pour cette société (${data.history_months} mois d'historique)${data.pipeline_pondere ? ` — pipeline CRM pondéré : ${Math.round(data.pipeline_pondere).toLocaleString("fr-FR")} €` : ""}. Complétez les hypothèses manuellement.`);
      } else {
        toast.success(`Données réelles appliquées (${data.history_months} mois d'historique)`);
      }
    } catch { toast.error("Impossible de charger les données réelles"); }
    finally { setPrefilling(false); }
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-4" data-testid="hypotheses-form">
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold text-white flex items-center gap-1.5 flex-1">
          <Settings2 className="w-4 h-4 text-[#f7c935]" /> Hypothèses
        </p>
        <Button size="sm" variant="outline" onClick={prefill} disabled={prefilling}
          className="border-white/10 text-slate-300 text-[10px] h-7" data-testid="prefill-base-data-btn">
          {prefilling ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" /> Depuis mes données</>}
        </Button>
      </div>

      <div>
        <Label>Nom du scénario</Label>
        <Input className={inputCls} value={name} onChange={e => setName(e.target.value)} data-testid="hyp-name" />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-[10px] text-[#f7c935] uppercase font-bold">Activité</legend>
        <div>
          <Label>CA mensuel de départ (HT €)</Label>
          <Num value={hyp.ca_base_mensuel} onChange={v => H("ca_base_mensuel", v)} testId="hyp-ca-base" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map(i => (
            <div key={i}>
              <Label>Croiss. An {i + 1} %</Label>
              <Num value={(hyp.croissance_pct || [])[i]} onChange={v => G(i, v)} step="0.5" testId={`hyp-croissance-${i + 1}`} />
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-[10px] text-[#f7c935] uppercase font-bold">Charges</legend>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Charges fixes / mois €</Label>
            <Num value={hyp.charges_fixes_mensuelles} onChange={v => H("charges_fixes_mensuelles", v)} testId="hyp-charges-fixes" />
          </div>
          <div>
            <Label>Charges variables % CA</Label>
            <Num value={hyp.charges_variables_pct} onChange={v => H("charges_variables_pct", v)} step="0.5" testId="hyp-charges-variables" />
          </div>
        </div>
        <div>
          <Label>Masse salariale initiale / mois € (coût employeur)</Label>
          <Num value={hyp.masse_salariale_mensuelle} onChange={v => H("masse_salariale_mensuelle", v)} testId="hyp-masse-salariale" />
        </div>
      </fieldset>

      <ListEditor
        title="Embauches prévues" testKey="embauche"
        items={hyp.embauches || []}
        onChange={items => H("embauches", items)}
        empty={{ label: "", mois_debut: 1, cout_mensuel: 3500 }}
        fields={[
          { key: "label", placeholder: "Poste", type: "text", w: "flex-1" },
          { key: "mois_debut", placeholder: "Mois", type: "number", w: "w-16" },
          { key: "cout_mensuel", placeholder: "€/mois", type: "number", w: "w-24" },
        ]} />

      <ListEditor
        title="Investissements" testKey="invest"
        items={hyp.investissements || []}
        onChange={items => H("investissements", items)}
        empty={{ label: "", montant: 10000, mois: 1, amortissement_annees: 5 }}
        fields={[
          { key: "label", placeholder: "Libellé", type: "text", w: "flex-1" },
          { key: "montant", placeholder: "€", type: "number", w: "w-20" },
          { key: "mois", placeholder: "Mois", type: "number", w: "w-14" },
          { key: "amortissement_annees", placeholder: "Amort. ans", type: "number", w: "w-16" },
        ]} />

      <fieldset className="space-y-2">
        <legend className="text-[10px] text-[#f7c935] uppercase font-bold">Financement</legend>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Emprunt — montant €</Label>
            <Num value={hyp.emprunt?.montant} onChange={v => HE("montant", v)} testId="hyp-emprunt-montant" />
          </div>
          <div>
            <Label>Taux annuel %</Label>
            <Num value={hyp.emprunt?.taux_pct} onChange={v => HE("taux_pct", v)} step="0.1" testId="hyp-emprunt-taux" />
          </div>
          <div>
            <Label>Durée (mois)</Label>
            <Num value={hyp.emprunt?.duree_mois} onChange={v => HE("duree_mois", v)} testId="hyp-emprunt-duree" />
          </div>
          <div>
            <Label>Mois de déblocage</Label>
            <Num value={hyp.emprunt?.mois_deblocage} onChange={v => HE("mois_deblocage", v)} testId="hyp-emprunt-deblocage" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Apport en capital €</Label>
            <Num value={hyp.apport} onChange={v => H("apport", v)} testId="hyp-apport" />
          </div>
          <div>
            <Label>Trésorerie initiale €</Label>
            <Num value={hyp.tresorerie_initiale} onChange={v => H("tresorerie_initiale", v)} testId="hyp-tresorerie-initiale" />
          </div>
        </div>
      </fieldset>

      <Button onClick={() => onSave(hyp, name)} disabled={saving}
        className="w-full bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="save-hypotheses-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer & recalculer</>}
      </Button>
    </div>
  );
}

function ListEditor({ title, items, onChange, empty, fields, testKey }) {
  const update = (i, key, val) => onChange(items.map((it, k) => k === i ? { ...it, [key]: val } : it));
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-[10px] text-[#f7c935] uppercase font-bold flex items-center gap-2">
        {title}
        <button type="button" onClick={() => onChange([...items, { ...empty }])}
          className="text-slate-400 hover:text-[#f7c935]" data-testid={`add-${testKey}-btn`}>
          <Plus className="w-3.5 h-3.5" />
        </button>
      </legend>
      {items.length === 0 && <p className="text-[10px] text-slate-600 italic">Aucun élément</p>}
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5" data-testid={`${testKey}-row-${i}`}>
          {fields.map(f => (
            <Input key={f.key} type={f.type} placeholder={f.placeholder}
              className={`${inputCls} h-8 text-xs px-2 ${f.w}`}
              value={it[f.key] ?? ""}
              onChange={e => update(i, f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
          ))}
          <button type="button" onClick={() => onChange(items.filter((_, k) => k !== i))} className="p-1">
            <Trash2 className="w-3.5 h-3.5 text-rose-400" />
          </button>
        </div>
      ))}
    </fieldset>
  );
}
